import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch } from "../store";
import { alertMessage } from "../actions/common";
import { errorHandler } from "app/shared/Service/errorHandler";
import { getActiveConfig } from "app/utils/constants/common";
import {
  getPredefinedVariantsAPI,
  getProductVariantsAPI,
  postProductVariantAPI,
  putProductVariantAPI,
} from "app/services/products";
import {
  PredefinedVariant,
  ProductVariant,
  UpsertProductVariantPayload,
} from "app/types/productVariants";
import i18n from "app/config/i18n";

interface ProductVariantsState {
  variants: ProductVariant[];
  predefinedVariants: PredefinedVariant[];
  includedPredefinedVariants: PredefinedVariant[];
  isListLoading: boolean;
  isPredefinedLoading: boolean;
  isSubmitting: boolean;
}

const initialState: ProductVariantsState = {
  variants: [],
  predefinedVariants: [],
  includedPredefinedVariants: [],
  isListLoading: false,
  isPredefinedLoading: false,
  isSubmitting: false,
};

export const productVariantsSlice = createSlice({
  name: "productVariants",
  initialState,
  reducers: {
    setVariants: (
      state,
      action: PayloadAction<{
        list: ProductVariant[];
        included: PredefinedVariant[];
      }>
    ) => {
      state.variants = action.payload.list;
      state.includedPredefinedVariants = action.payload.included;
    },
    setPredefinedVariants: (
      state,
      action: PayloadAction<PredefinedVariant[]>
    ) => {
      state.predefinedVariants = action.payload;
    },
    setListLoading: (state, action: PayloadAction<boolean>) => {
      state.isListLoading = action.payload;
    },
    setPredefinedLoading: (state, action: PayloadAction<boolean>) => {
      state.isPredefinedLoading = action.payload;
    },
    setSubmitting: (state, action: PayloadAction<boolean>) => {
      state.isSubmitting = action.payload;
    },
    clearVariants: (state) => {
      state.variants = [];
      state.includedPredefinedVariants = [];
    },
  },
});

export const {
  setVariants,
  setPredefinedVariants,
  setListLoading,
  setPredefinedLoading,
  setSubmitting,
  clearVariants,
} = productVariantsSlice.actions;

export default productVariantsSlice.reducer;

export const getPredefinedVariantsAction = () => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setPredefinedLoading(true));
      const { data } = await getPredefinedVariantsAPI();
      dispatch(setPredefinedLoading(false));
      dispatch(setPredefinedVariants(data?.data || []));
    } catch (error) {
      dispatch(setPredefinedLoading(false));
      dispatch(alertMessage(errorHandler(error), "error"));
    }
  };
};

export const getProductVariantsAction = (productId: number) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setListLoading(true));
      const { data } = await getProductVariantsAPI(storeId, productId);
      dispatch(setListLoading(false));
      dispatch(
        setVariants({
          list: data?.data || [],
          included: data?.included?.predefinedVariants || [],
        })
      );
    } catch (error) {
      dispatch(setListLoading(false));
      dispatch(alertMessage(errorHandler(error), "error"));
    }
  };
};

export const postProductVariantAction = ({
  productId,
  payload,
}: {
  productId: number;
  payload: UpsertProductVariantPayload;
}) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setSubmitting(true));
      const { data } = await postProductVariantAPI(storeId, productId, payload);
      dispatch(setSubmitting(false));
      if (data?.data) {
        dispatch(alertMessage(i18n.t("common.variantCreated"), "success"));
        dispatch(getProductVariantsAction(productId));
        return data.data;
      }
    } catch (error) {
      dispatch(setSubmitting(false));
      dispatch(alertMessage(errorHandler(error), "error"));
    }
  };
};

export const putProductVariantAction = ({
  productId,
  variantId,
  payload,
}: {
  productId: number;
  variantId: number;
  payload: UpsertProductVariantPayload;
}) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setSubmitting(true));
      const { data } = await putProductVariantAPI(
        storeId,
        productId,
        variantId,
        payload
      );
      dispatch(setSubmitting(false));
      if (data?.data) {
        dispatch(alertMessage(i18n.t("common.variantUpdated"), "success"));
        dispatch(getProductVariantsAction(productId));
        return data.data;
      }
    } catch (error) {
      dispatch(setSubmitting(false));
      dispatch(alertMessage(errorHandler(error), "error"));
    }
  };
};
